import { useEffect, useRef, useState } from 'react'
import { filesAPI } from 'services/swagger-api'

interface UseDocumentBlobResult {
  blobUrl: string | null
  isLoading: boolean
}

export const useDocumentBlob = (
  storedFileId?: string,
  externalBlobUrl?: string | null,
  mimeType?: string
): UseDocumentBlobResult => {
  const [internalBlobUrl, setInternalBlobUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const blobUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (externalBlobUrl || !storedFileId) return

    let cancelled = false
    const fetchDocument = async () => {
      setIsLoading(true)
      try {
        const response = await filesAPI.getTemporaryUrl({ storedFileId })
        if (cancelled || !response.isSuccess || !response.url) return

        const fileResponse = await fetch(response.url)
        const originalBlob = await fileResponse.blob()
        const blob = new Blob([originalBlob], { type: mimeType || 'application/pdf' })
        const url = URL.createObjectURL(blob)

        if (!cancelled) {
          blobUrlRef.current = url
          setInternalBlobUrl(url)
        } else {
          URL.revokeObjectURL(url)
        }
      } catch (error) {
        console.error('Error loading document:', error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchDocument()
    return () => {
      cancelled = true
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [storedFileId, externalBlobUrl, mimeType])

  return {
    blobUrl: externalBlobUrl ?? internalBlobUrl,
    isLoading
  }
}
